import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { take } from 'rxjs';

import { TranslatePipe } from '@core/i18n-legacy';
import { TellentAccessService, TellentGrowRedirectService } from '@core/tellent-logic';
import { PositionDetailsSkillsAccessService } from '@core/tellent-positions-logic';
import { TellentPosition } from '@core/tellent-positions-types';
import { AddSkillsDropdownComponent, ProficiencyPanelComponent } from '@core/tellent-positions-ui';
import { TellentSkillsDataFacade } from '@core/tellent-skills-logic';
import {
  AssignedSkill,
  MappingSkill,
  Proficiency,
  Skill,
  SkillLevel,
  SkillMapping,
} from '@core/tellent-skills-types';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ConnectedOverlayDirective,
  DialogRef,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  EmptyStateComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';

import { PositionDetailsSkillsNotificationsService } from './position-details-skills-notifications.service';
import { PositionsDialogsService } from '../../../services/positions-dialogs.service';

@Component({
  selector: 'tlc-position-details-skills',
  templateUrl: './position-details-skills.component.html',
  styleUrls: ['./position-details-skills.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    AddSkillsDropdownComponent,
    ProficiencyPanelComponent,
    EmptyStateComponent,
    IconButtonComponent,
    BadgeComponent,
    IconComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    OverlayOriginDirective,
    PlaceloaderComponent,
  ],
  providers: [PositionDetailsSkillsNotificationsService, TellentGrowRedirectService],
})
export class PositionDetailsSkillsComponent {
  private readonly skillsFacade = inject(TellentSkillsDataFacade);
  private readonly skillsAccessService = inject(PositionDetailsSkillsAccessService);
  private readonly growRedirectService = inject(TellentGrowRedirectService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(DialogRef);
  private readonly positionsDialogsService = inject(PositionsDialogsService);

  private readonly availableSkillEntity = this.skillsFacade.availableSkillEntity;

  protected readonly positionData = input.required<TellentPosition | null>();

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly isAssigning = signal(false);
  protected readonly updatingSkillId = signal<number | null>(null);
  protected readonly removingSkillId = signal<number | null>(null);
  protected readonly removingSkillMappingId = signal<number | null>(null);

  protected readonly accessState = this.skillsAccessService.accessState;
  protected readonly canManageUsers = inject(TellentAccessService).canManageTellentUsers;

  protected readonly positionSkillEntity = this.skillsFacade.positionSkillEntity;
  protected readonly assignedSkills = computed<AssignedSkill[]>(
    () => this.positionSkillEntity()?.skills ?? [],
  );
  protected readonly assignedSkillMappings = computed<SkillMapping[]>(
    () => this.positionSkillEntity()?.skillMappings ?? [],
  );
  protected readonly assignedSkillIds = computed(() => this.assignedSkills().map(s => s.skill.id));
  protected readonly assignedSkillMappingIds = computed(() =>
    this.assignedSkillMappings().map(m => m.id),
  );

  public readonly availableSkills = computed(() => this.availableSkillEntity()?.skills ?? []);
  public readonly availableSkillMappings = computed(
    () => this.availableSkillEntity()?.skillMappings ?? [],
  );

  constructor() {
    inject(PositionDetailsSkillsNotificationsService);

    effect(() => {
      if (this.accessState() !== 'ok') return;
      this.skillsFacade.fetchAvailableSkills();
    });

    effect(() => {
      if (this.accessState() !== 'ok') return;
      const guid = this.positionData()?.guid;
      if (guid) {
        this.skillsFacade.clearPositionSkills();
        this.skillsFacade.fetchPositionSkills(guid);
      }
    });

    effect(() => {
      if (!this.skillsFacade.isPositionSkillsPending()) {
        this.isAssigning.set(false);
        this.updatingSkillId.set(null);
        this.removingSkillId.set(null);
        this.removingSkillMappingId.set(null);
      }
    });
  }

  public onCreateNewSkillClicked(): void {
    this.growRedirectService.createNewSkillRedirect();
  }

  public onManageSkillMappingsClicked(skillMappingId?: number): void {
    this.growRedirectService.manageSkillMappingsRedirect(skillMappingId);
  }

  public onSkillSelected(event: { skill: Skill; proficiency: Proficiency }): void {
    const positionGuid = this.positionData()?.guid;
    if (positionGuid) {
      this.isAssigning.set(true);
      this.skillsFacade.assignSkill(positionGuid, event.skill.id, event.proficiency.id);
    }
  }

  public onSkillMappingSelected(event: { skillMapping: SkillMapping; level: SkillLevel }): void {
    const positionGuid = this.positionData()?.guid;
    if (positionGuid) {
      this.isAssigning.set(true);
      this.skillsFacade.assignSkillMapping(positionGuid, event.skillMapping.id, event.level.id);
    }
  }

  public getProficiencyForMappingSkill(
    mappingItem: SkillMapping,
    skill: MappingSkill,
  ): Proficiency | null {
    const level = mappingItem.levels?.[0];
    if (!level?.skillProficiencies) return null;

    const ref = level.skillProficiencies.find(sp => sp.skillId === skill.skillId);
    if (!ref) return null;

    return skill.proficiencies?.find(p => p.id === ref.proficiencyId) ?? null;
  }

  public getAvailableSkill(skillId: number): Skill | undefined {
    return this.availableSkills().find(s => s.id === skillId);
  }

  public onUpdateSkillProficiency(item: AssignedSkill, proficiency: Proficiency): void {
    if (proficiency.id === item.proficiency?.id) return;
    const positionGuid = this.positionData()?.guid;
    if (positionGuid) {
      this.updatingSkillId.set(item.skill.id);
      this.skillsFacade.updateSkill(positionGuid, item.skill.id, proficiency.id);
    }
  }

  public onRemoveSkillMapping(item: SkillMapping): void {
    const position = this.positionData();
    if (!position) return;

    const levelId = item.levels?.[0]?.id;
    if (!levelId) return;

    this.positionsDialogsService
      .openUnassignSkillMappingDialog(position, item)
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.removingSkillMappingId.set(item.id);
          this.skillsFacade.unassignSkillMapping(position.guid, item.id, levelId);
        }
      });
  }

  public onRemoveSkill(item: AssignedSkill): void {
    const position = this.positionData();
    if (!position) return;

    this.positionsDialogsService
      .openUnassignSkillDialog(position, item)
      .pipe(take(1))
      .subscribe(confirmed => {
        if (confirmed) {
          this.removingSkillId.set(item.skill.id);
          this.skillsFacade.unassignSkill(position.guid, item.skill.id);
        }
      });
  }

  public navigateToBilling(): void {
    this.dialogRef.close();
    this.router.navigate(['billing']);
  }
}
